import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

import {
  ActiveDialog,
  GlobalDialogConfig,
  GlobalDialogDataDefinition,
  GlobalDialogsStore,
  GlobalDialogType,
} from './global-dialogs.store.types';

const GLOBAL_DIALOG_DATA = 'GLOBAL_DIALOG_DATA-V2';

export const useGlobalDialogsStore = create<GlobalDialogsStore>()(
  devtools(
    persist(
      (set, get) => ({
        config: {
          [GlobalDialogType.CampaignPhoneReachOut]: { skipFollowUp: false },
          [GlobalDialogType.ShouldConnectEmail]: null,
          [GlobalDialogType.CandidateOutreach]: null,
          [GlobalDialogType.EnrichVacancy]: null,
          [GlobalDialogType.CandidateFeedback]: null,
          [GlobalDialogType.DiscardChangesModal]: null,
          [GlobalDialogType.SelectAtsVacancyForCampaign]: null,
          [GlobalDialogType.ReachoutInterviewModal]: null,
          [GlobalDialogType.NylasCalendarManageConnectionModal]: null,
          [GlobalDialogType.RejectCandidate]: null,
          [GlobalDialogType.RjCommBulk]: null,
        },
        dialogs: {},
        activeDialogs: [],

        setGlobalDialogConfig: <T extends GlobalDialogType>(type: T, config: GlobalDialogConfig<T>) => {
          set({ config: { ...get().config, [type]: config } });
        },
        getGlobalDialogConfig: <T extends GlobalDialogType>(type: T) => {
          return get().config[type];
        },
        setActiveDialogs: (dialogs: ActiveDialog[]) => {
          set({ activeDialogs: dialogs });
        },
        addActiveDialog: (dialog: ActiveDialog) => {
          const state = get();
          const existingIndex = state.activeDialogs.findIndex((d) => d.type === dialog.type);

          if (existingIndex >= 0) {
            const newDialogs = [...state.activeDialogs];
            newDialogs[existingIndex] = dialog;

            set({ activeDialogs: newDialogs });
          } else {
            set({ activeDialogs: [...state.activeDialogs, dialog] });
          }
        },
        removeActiveDialog: (dialogType: GlobalDialogType) => {
          const state = get();
          const newDialogs = state.activeDialogs.filter((d) => d.type !== dialogType);

          set({ activeDialogs: newDialogs });
        },
        set: <T extends GlobalDialogType>(type: T, data: GlobalDialogDataDefinition[T], key: string) => {
          const allData = get().dialogs;
          const typeData: { [key: string]: GlobalDialogDataDefinition[T] } = allData[type] ?? {};
          typeData[key] = data;

          set({ dialogs: { ...allData, [type]: typeData } });
        },
        get: <T extends GlobalDialogType>(type: T, key: string) => {
          return get().dialogs[type]?.[key];
        },
        getTypeData: <T extends GlobalDialogType>(type: T) => {
          return get().dialogs[type];
        },
        remove: <T extends GlobalDialogType>(type: T, key: string) => {
          const allData = get().dialogs;
          const typeData = allData[type];

          if (!typeData) {
            return;
          }

          delete typeData[key];

          if (Object.keys(typeData).length === 0) {
            delete allData[type];

            set({ dialogs: allData });
          } else {
            set({
              dialogs: { ...allData, [type]: typeData },
            });
          }
        },
      }),
      {
        name: GLOBAL_DIALOG_DATA,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({ dialogs: state.dialogs, config: state.config }),
      },
    ),
    { name: 'global-dialogs' },
  ),
);

export const setUserScopedDialogStorage = (userId: number | undefined) => {
  const storageKey = userId ? `${GLOBAL_DIALOG_DATA}_USER_${userId}` : GLOBAL_DIALOG_DATA;

  useGlobalDialogsStore.persist.setOptions({
    name: storageKey,
  });

  useGlobalDialogsStore.persist.rehydrate();
};
